// Global constants — values sourced from DESIGN.md (§3 map, §4 territory,
// §5 economy, §6 construction, §12 tick model).

export const TICK_MS = 100; // 10 Hz fixed sim tick
export const TICKS_PER_SECOND = 1000 / TICK_MS;

export const MAP_SIZES = { small: 128, medium: 192, large: 256 } as const;
export type MapSizeName = keyof typeof MAP_SIZES;

// Terrain type ids (Uint8Array cells)
export const enum Terrain {
  Water = 0,
  Sand = 1,
  Grass = 2,
  Forest = 3, // grass with tree cover (walkable; reverts to Grass when cleared)
  Rock = 4, // mountain: mines only, blocks movement
}

export const TERRAIN_COUNT = 5;

export function isWalkable(t: Terrain): boolean {
  return t === Terrain.Sand || t === Terrain.Grass || t === Terrain.Forest;
}

// Hex sector lattice (DESIGN.md §3.1): tiles map to nearest center of a
// staggered lattice — cells are hexagon-ish, ~12 tiles across, exact-integer math.
export const HEX_COL_SPACING = 12;
export const HEX_ROW_SPACING = 10;

// Elevation bands (u8 elevation 0..255 -> terrain)
export const ELEV_WATER = 78;
export const ELEV_SAND = 88;
export const ELEV_ROCK = 178;

export const MAX_ENTITIES = 4096;
export const CHECKSUM_INTERVAL_TICKS = 50;

// ---- units (DESIGN.md §5.4, §4.1–4.2, §7.3) ----

/** must match data/units.json order: kind = array index + 1 */
export const enum UnitKind {
  Porter = 1,
  Surveyor = 2,
  Scout = 3,
  Shieldbearer = 4,
  Axeman = 5,
  Archer = 6,
  Arbalist = 7,
  Sapper = 8,
  Bandit = 9,
}

export const enum UnitState {
  Idle = 0,
  ToPickup = 1,
  ToDeliver = 2,
  Returning = 3,
  ToJob = 4,
  Working = 5,
  Moving = 6, // military move order (flow-field / steer); attack-move via orderAttack flag
  Attacking = 7, // has an acquired target (chase + attack)
  Garrisoned = 8, // inside a watchtower (invisible, safe)
}

export const PORTER_SPEED_FP = 6554; // 0.1 tiles/tick = 1.0 tiles/s in Q16.16
export const ROAD_SPEED_NUM = 3; // on-road speed x1.5 (ratio 3/2, integer math)
export const ROAD_SPEED_DEN = 2;
export const SURVEYOR_SPEED_FP = 6554;

export const CLAIM_TICKS = 300; // 30s (DESIGN.md §4.1)
export const SURVEY_TICKS = 200; // 20s (DESIGN.md §4.2)

// ---- military (DESIGN.md §7) ----

export const enum Owner {
  Player = 0,
  Enemy = 1, // AI opponent (M4)
  Bandit = 2, // neutral hostiles (§7.7)
}

export const MELEE_RANGE_FP = 98304; // 1.5 tiles: adjacent incl. diagonal
export const RANGED_SLACK_FP = 32768; // +0.5 tile grace on ranged attacks
export const COMBAT_CELL = 8; // spatial grid cell size (tiles)
export const RECENT_DAMAGE_TICKS = 20; // "in combat" window (shield wall, flee)
export const HP_REGEN_NONE = 0; // no passive regen in v1
export const CAPTURE_REACTIVATE_HP_PCT = 50; // §7.6: inherited ruins at 50% HP
export const BURN_TICK_INTERVAL = 10; // firebomb DoT applies every 10 ticks
export const UNDER_ATTACK_ALERT_COOLDOWN = 100; // global alert cooldown (ticks)

// flow fields (DESIGN.md §12.2): shared per group move order, budgeted build
export const FLOW_MAX_FIELDS = 8;
export const FLOW_FIELDS_PER_TICK = 2; // ≤2 in-progress field builds advance per tick
export const FLOW_EXPAND_PER_TICK = 8000; // node pops budget shared per tick

// ---- economy (DESIGN.md §5.3, §5.5, §5.6) ----

export const INPUT_BUFFER_TARGET = 4; // per input slot
export const OUTPUT_BUFFER_CAP = 4; // production pauses when full
export const OUTPUT_FLUSH_AT = 3; // ship surplus to warehouse from here
export const DISPATCH_INTERVAL_TICKS = 5;
export const PORTER_SPAWN_INTERVAL_TICKS = 10;

export const DONKEY_CARRY = 3; // wares per trip on a donkey porter (DESIGN.md §5.5)
export const BOOST_CAP_TICKS = 3000; // food boost stack cap: 5 min (DESIGN.md §5.6)
export const BOOST_REFILL_BELOW_TICKS = 600; // request more food under 60s remaining
export const MINE_UNBOOSTED_NUM = 5; // unboosted mines run at 60% speed:
export const MINE_UNBOOSTED_DEN = 3; //   cycle * 5/3 (DESIGN.md §5.6)
export const STARVED_ALERT_TICKS = 300; // input-starved for 30s -> alert
export const ALERT_COOLDOWN_TICKS = 1200; // per-building alert repeat cooldown

// placement rules (commands.ts; thresholds for data-driven flags)
export const WELL_MAX_ELEVATION = 150; // needsGroundwater
export const FISHERY_MIN_WATER = 5; // water tiles within radius (needsShore)
export const HUNTER_MIN_TREES = 8; // habitat tiles for production
export const FARM_MIN_FIELDS = 6; // free grass tiles for production
export const FORESTER_PLANT_DENSITY = 60; // treeDensity added per planted tile
export const FORESTER_MAX_DENSITY = 180;

// ---- buildings (DESIGN.md §6.1) ----

export const enum BuildingState {
  Blueprint = 0, // awaiting material deliveries
  Constructing = 1, // materials done; awaiting/being built by surveyor
  AwaitWorker = 2, // built; production buildings await worker+tool
  Active = 3,
  Disabled = 4, // hex flipped neutral: frozen until re-claimed (DESIGN.md §7.6)
}

// ---- fog of war (sim-side from M3; DESIGN.md §4.3) ----

export const enum FogState {
  Unexplored = 0,
  Explored = 1, // static memory
  Visible = 2, // live
}

export const UNIT_VISION_TILES = 8;
export const BUILDING_VISION_TILES = 8;
export const FOG_INTERVAL_TICKS = 5; // visibility recompute cadence
